/**
 * Package & Logistics — weight, dimensions, quantity.
 * Hidden if all values are null/0.
 *
 * TikTok: Weight and Dimensions are inline-editable.
 * Quantity stays read-only (inventory editing is Epic 05).
 *
 * Note: package_weight is stored in grams (from TikTok API).
 * Display and edit in grams to avoid conversion errors.
 */

import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig, formatDimensions, Divider, SettingsRow } from "./shared";
import { EditableSettingsRow } from "./EditableSettingsRow";

export function MarketplacePackageWidget({ section, formData }: ExperienceWidgetProps) {
  const channel = section.description || "";
  const config = getChannelConfig(channel);
  const isTikTok = channel === "TikTok Shop";

  const weight = formData?.[config.weight] as number | undefined;
  const quantity = formData?.[config.quantity] as number | undefined;
  const dims = formatDimensions(formData || {}, config);
  const rawDims = config.dimensions ? formData?.[config.dimensions] : null;

  // Hide if all empty
  if (!weight && !quantity && !dims) return null;

  // ── TikTok: editable rows ─────────────────────────────────────

  if (isTikTok) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
          Package & Logistics
        </h3>
        <div className="rounded-2xl overflow-hidden bg-card">
          {/* Weight — editable, stored in grams */}
          <EditableSettingsRow
            label="Weight"
            value={weight}
            fieldKey="package_weight"
            editType="number"
            suffix="g"
            placeholder="0"
          />
          <Divider />

          {/* Dimensions — editable L × W × H */}
          <EditableSettingsRow
            label="Dimensions"
            value={rawDims}
            fieldKey="package_dimensions"
            editType="dimensions"
            suffix="cm"
          />

          {/* Quantity moved to separate MarketplaceInventoryWidget (Epic 05) */}
        </div>
      </div>
    );
  }

  // ── Lazada: read-only rows (unchanged) ────────────────────────

  const rows: Array<{ label: string; value: string }> = [];
  if (weight) rows.push({ label: "Weight", value: `${weight} kg` });
  if (dims) rows.push({ label: "Dimensions", value: dims });
  if (quantity != null) rows.push({ label: "Quantity", value: String(quantity) });

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Package & Logistics
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {rows.map((row, i) => (
          <div key={row.label}>
            {i > 0 && <Divider />}
            <SettingsRow label={row.label} value={row.value} />
          </div>
        ))}
      </div>
    </div>
  );
}
